import { useEffect, useRef } from 'react'
import { Button, Heading } from '@design-systems/canvas-ui'
import Icon, { RefreshIcon } from '@design-systems/react-icons'
import { useTranslation } from 'react-i18next'
import { TRANSLATION_KEYS as keys } from 'locales/translation-keys'
import { usePageView } from 'utils/analytics'
import { MPMLayout } from 'components/layout'

type TransientErrorProps = {
    onRetry?: () => void
}

/**
 * Shown when User Details fails due to a transient backend/infrastructure
 * problem (Workday outage, token-refresh blip, 5xx, timeout, empty/non-JSON
 * response). This is intentionally distinct from <Unauthorized />, which
 * represents a genuine access-control decision.
 *
 * Accessibility:
 * - Error heading receives focus on mount so TalkBack/VoiceOver announces it.
 * - The live region announces the error state to screen readers.
 * - The retry button is a real <button> with a clear accessible name.
 * - No color-only differentiation; the heading text and icon provide context.
 */
export const TransientError = ({ onRetry }: TransientErrorProps) => {
    const { t } = useTranslation()
    const { trackPageView } = usePageView('transient-error')
    const headingRef = useRef<HTMLHeadingElement>(null)

    useEffect(() => {
        trackPageView()
        // Move focus to the heading so screen readers announce the error state.
        headingRef.current?.focus()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Empty array — only track once on mount

    return (
        <MPMLayout breadcrumbsEnabled={false}>
            {/* ARIA live region announces the error state to screen readers. */}
            <div
                role="status"
                aria-live="polite"
                aria-atomic="true"
                className="hc8-sr-only"
            >
                {t(keys.transientError.title)}
            </div>
            <section
                className="hc8-mt-3x"
                aria-labelledby="transient-error-heading"
            >
                <Heading
                    id="transient-error-heading"
                    ref={headingRef}
                    size={4}
                    className="hc8-mb-3x hc8-clr-base"
                    // tabIndex={-1} allows programmatic focus without adding
                    // the element to the natural tab order.
                    tabIndex={-1}
                >
                    {t(keys.transientError.title)}
                </Heading>

                <p className="hc8-mb-3x hc8-clr-base">
                    {t(keys.transientError.message)}
                </p>

                {onRetry && (
                    <Button
                        variant="primary"
                        onClick={onRetry}
                        aria-label={t(keys.transientError.retryButton)}
                    >
                        <Icon svg={RefreshIcon} aria-hidden />
                        <span>{t(keys.transientError.retryButton)}</span>
                    </Button>
                )}
            </section>
        </MPMLayout>
    )
}
